// src/Analytics.js - Page analytics API avec graphiques
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './styles/Dashboard.css';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState(24);

  useEffect(() => {
    loadAnalytics();
    const interval = setInterval(loadAnalytics, 60000); // Refresh toutes les minutes
    return () => clearInterval(interval);
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/analytics/dashboard', {
        params: { hours: timeRange },
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div style={{ padding: '30px', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem' }}>⏳</div>
        <p>Chargement des analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '30px' }}>
        <div className="management-alert error">
          <span>⚠️</span>
          {error}
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const topEndpoints = data?.topEndpoints || [];
  const callsByMember = data?.callsByMember || [];
  const callsByHour = data?.callsByHour || [];
  const statusCodes = data?.statusCodes || [];

  // Préparer les données pour les graphiques
  const endpointsChartData = {
    labels: topEndpoints.map(e => e.endpoint.replace('/api/', '')),
    datasets: [{
      label: 'Nombre d\'appels',
      data: topEndpoints.map(e => e.call_count),
      backgroundColor: 'rgba(102, 126, 234, 0.8)',
      borderColor: 'rgba(102, 126, 234, 1)',
      borderWidth: 1,
    }]
  };

  const hourlyChartData = {
    labels: callsByHour.map(h => new Date(h.hour).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })),
    datasets: [
      {
        label: 'Appels réussis',
        data: callsByHour.map(h => h.call_count - h.error_count),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.3,
      },
      {
        label: 'Erreurs',
        data: callsByHour.map(h => h.error_count),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.3,
      }
    ]
  };

  const statusCodesChartData = {
    labels: statusCodes.map(s => `${s.status_code} (${s.count})`),
    datasets: [{
      data: statusCodes.map(s => s.count),
      backgroundColor: statusCodes.map(s => {
        if (s.status_code >= 200 && s.status_code < 300) return 'rgba(75, 192, 192, 0.8)';
        if (s.status_code >= 400 && s.status_code < 500) return 'rgba(255, 206, 86, 0.8)';
        return 'rgba(255, 99, 132, 0.8)';
      }),
    }]
  };

  return (
    <div className="management-page">
      <div className="management-page__inner">
        {/* Header */}
        <header className="management-header glass-panel">
          <div className="management-header__content">
            <h1 className="management-title">
              <span className="management-title__icon">📊</span>
              Analytics API
            </h1>
            <p className="management-subtitle">
              Monitoring en temps réel des appels API, performance et utilisation par token.
            </p>
            <div className="management-meta">
              <span className="management-badge">Dernières {timeRange}h</span>
              <span className="management-badge">Mise à jour automatique</span>
            </div>
          </div>
          <div className="management-header__actions">
            <select
              className="mgmt-input"
              style={{ width: 'auto', marginRight: '10px' }}
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
            >
              <option value={1}>1 heure</option>
              <option value={6}>6 heures</option>
              <option value={24}>24 heures</option>
              <option value={168}>7 jours</option>
            </select>
            <button 
              type="button" 
              className="mgmt-btn mgmt-btn--outline" 
              onClick={loadAnalytics}
            >
              🔄 Actualiser
            </button>
          </div>
        </header>

        {/* Stats cards */}
        <section className="management-stats">
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Total appels</span>
            <span className="management-stat-value">{stats.total_calls || 0}</span>
            <span className="management-stat-meta">Dernières {timeRange}h</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Utilisateurs</span>
            <span className="management-stat-value">{stats.unique_users || 0}</span>
            <span className="management-stat-meta">Tokens actifs</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Endpoints</span>
            <span className="management-stat-value">{stats.unique_endpoints || 0}</span>
            <span className="management-stat-meta">Routes utilisées</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Temps réponse</span>
            <span className="management-stat-value">
              {stats.avg_response_time ? Math.round(stats.avg_response_time) : 0}ms
            </span>
            <span className="management-stat-meta">Moyenne</span>
          </div>
        </section>

        {/* Graphiques */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px', marginTop: '24px' }}>
          
          {/* Top Endpoints */}
          <section className="management-section glass-panel">
            <div className="management-section__head">
              <h2 className="management-section__title">📍 Endpoints les plus appelés</h2>
            </div>
            <div style={{ padding: '20px', height: '400px' }}>
              {topEndpoints.length > 0 ? (
                <Bar 
                  data={endpointsChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      title: { display: false }
                    },
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', paddingTop: '100px', color: '#999' }}>
                  Aucune donnée disponible
                </div>
              )}
            </div>
          </section>

          {/* Appels par heure */}
          <section className="management-section glass-panel">
            <div className="management-section__head">
              <h2 className="management-section__title">⏰ Appels par heure</h2>
            </div>
            <div style={{ padding: '20px', height: '400px' }}>
              {callsByHour.length > 0 ? (
                <Line 
                  data={hourlyChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'top' }
                    },
                    scales: {
                      y: { beginAtZero: true }
                    }
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', paddingTop: '100px', color: '#999' }}>
                  Aucune donnée disponible
                </div>
              )}
            </div>
          </section>

          {/* Status codes */}
          <section className="management-section glass-panel">
            <div className="management-section__head">
              <h2 className="management-section__title">📊 Codes de statut HTTP</h2>
            </div>
            <div style={{ padding: '20px 20px 40px', height: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {statusCodes.length > 0 ? (
                <div style={{ width: '300px', height: '300px' }}>
                  <Doughnut 
                    data={statusCodesChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'bottom' }
                      }
                    }}
                  />
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: '#999' }}>
                  Aucune donnée disponible
                </div>
              )}
            </div>
          </section>

          {/* Appels par token */}
          <section className="management-section glass-panel">
            <div className="management-section__head">
              <h2 className="management-section__title">🔑 Utilisation par token</h2>
            </div>
            <div style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
              {callsByMember.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#666' }}>Token</th>
                      <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>Appels</th>
                      <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>Temps moy.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callsByMember.map((member, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px' }}>
                          <strong>{member.token_name || `User #${member.member_id}`}</strong>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#667eea', fontWeight: '600' }}>
                          {member.call_count}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#999' }}>
                          {Math.round(member.avg_response_time)}ms
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', paddingTop: '50px', color: '#999' }}>
                  Aucune donnée disponible
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Détails des endpoints */}
        <section className="management-section glass-panel" style={{ marginTop: '24px' }}>
          <div className="management-section__head">
            <h2 className="management-section__title">📋 Détails par endpoint</h2>
            <p className="management-section__subtitle">
              Performance et fréquence d'utilisation des endpoints
            </p>
          </div>
          <div style={{ padding: '20px' }}>
            {topEndpoints.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#666' }}>Endpoint</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>Appels</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>Moy.</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>Min</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>Max</th>
                  </tr>
                </thead>
                <tbody>
                  {topEndpoints.map((endpoint, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                        {endpoint.endpoint}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#667eea' }}>
                        {endpoint.call_count}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#333' }}>
                        {Math.round(endpoint.avg_response_time)}ms
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#4caf50' }}>
                        {Math.round(endpoint.min_response_time)}ms
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#ff5722' }}>
                        {Math.round(endpoint.max_response_time)}ms
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <p style={{ fontSize: '2rem', margin: '0 0 12px 0' }}>📭</p>
                <p>Aucun appel API enregistré dans les dernières {timeRange}h</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Analytics;

